<template>
  <sistema>
    <container :isModal="false" :loading="!stateReady">
      <template v-slot:header>
        <h3>Preços RNTRC</h3>
      </template>
      <template v-slot:body>
        <p class="rntrc-ajuda">
          Sem cliente selecionado, a tela mostra a <b>tabela padrão</b> (vale
          para todo cliente sem preço negociado). Com um cliente, mostra o
          <b>preço negociado</b> dele; onde não houver linha própria, vale o
          padrão. O preço entra como snapshot na criação da solicitação: mudar a
          tabela não altera o passado.
        </p>
        <entity-selector-cliente
          :idClienteFiltered="cliente?.idCliente ?? undefined"
          :canEdit="true"
          @confirm-selection="confirmSelectionCliente"
          :parentLoading="stateLoading"
          label="Cliente (vazio = tabela padrão)"
        />

        <DataTable
          :value="matriz"
          :loading="stateLoading"
          class="p-datatable-sm p-mt-3"
          responsiveLayout="stack"
          breakpoint="60rem"
          :stripedRows="true"
        >
          <template #header>
            <div class="search-result-header">
              <span class="search-result-title" v-text="tituloMatriz" />
              <span class="search-result-count">
                vigente em {{ formatDate(hoje) }} (ou a próxima vigência)
              </span>
            </div>
          </template>
          <Column header="Serviço">
            <template #body="{ data }">
              <b v-text="data.descricao" />
            </template>
          </Column>
          <Column header="Unidade" style="width: 9rem">
            <template #body="{ data }">
              {{ getUnidadePrecoLabel(data.unidade) }}
            </template>
          </Column>
          <Column header="Veículo trator / valor único">
            <template #body="{ data }">
              <rntrc-preco-celula
                :celula="data.principal"
                @editar="editarCelula(data, data.porVeiculo ? 'AUT' : null)"
              />
            </template>
          </Column>
          <Column header="Carreta (inclusão)">
            <template #body="{ data }">
              <rntrc-preco-celula
                v-if="data.porVeiculo"
                :celula="data.carreta"
                @editar="editarCelula(data, 'IMP')"
              />
              <span v-else class="rntrc-muted">---</span>
            </template>
          </Column>
        </DataTable>

        <DataTable
          :value="linhasEscopo"
          :loading="stateLoading"
          class="p-datatable-sm p-mt-4"
          responsiveLayout="stack"
          breakpoint="60rem"
          :stripedRows="true"
        >
          <template #header>
            <div class="search-result-header">
              <span class="search-result-title"
                >Vigências cadastradas
                {{ cliente ? "para o cliente" : "da tabela padrão" }}</span
              >
              <Button
                label="Novo preço"
                icon="pi pi-plus"
                class="p-button-rounded p-button-sm"
                @click="novoPreco()"
              />
            </div>
          </template>
          <template #empty>
            <div class="p-text-center p-p-3">
              Nenhum preço cadastrado{{
                cliente ? " para o cliente: vale a tabela padrão" : ""
              }}.
            </div>
          </template>
          <Column header="Serviço">
            <template #body="{ data }">
              {{ getTipoServicoLabel(data.flTipoServico) }}
            </template>
          </Column>
          <Column header="Veículo">
            <template #body="{ data }">
              {{ getServicoVeiculoLabel(data.flServicoVeiculo) || "qualquer" }}
            </template>
          </Column>
          <Column header="Unidade">
            <template #body="{ data }">
              {{ getUnidadePrecoLabel(data.flUnidade) }}
            </template>
          </Column>
          <Column header="Valor">
            <template #body="{ data }">
              <b>{{ formatMoeda(data.valor) }}</b>
            </template>
          </Column>
          <Column header="Vigência">
            <template #body="{ data }">
              {{ formatDate(data.dataInicioVigencia) }} a
              {{
                data.dataFimVigencia
                  ? formatDate(data.dataFimVigencia)
                  : "indeterminado"
              }}
              <span
                v-if="isVigente(data)"
                class="aet-badge aet-badge--success p-ml-1"
                >vigente</span
              >
            </template>
          </Column>
          <Column style="width: 7rem; text-align: right">
            <template #body="{ data }">
              <button-group>
                <Button
                  icon="pi pi-pencil"
                  class="p-button-rounded p-button-success"
                  v-title="'Editar'"
                  @click="editarLinha(data)"
                />
                <Button
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-danger"
                  v-title="'Excluir'"
                  @click="removerLinha(data)"
                />
              </button-group>
            </template>
          </Column>
        </DataTable>

        <Dialog
          v-model:visible="edicao.aberta"
          :modal="true"
          :draggable="false"
          :style="{ width: '95vw', maxWidth: '640px' }"
          :header="
            (edicao.entity.idRntrcPrecoServico ? 'Editar' : 'Novo') +
              ' preço ' +
              (cliente ? 'do cliente ' + cliente.nome : 'padrão')
          "
        >
          <form @submit.prevent="salvar" id="rntrc-preco-form">
            <div class="p-formgrid p-grid p-mt-2">
              <div class="p-field p-fluid p-col-12 p-md-6">
                <input-with-label label="Serviço" v-slot="l" :required="true">
                  <Dropdown
                    :id="l.id"
                    v-model="edicao.entity.flTipoServico"
                    :options="tiposDisponiveis"
                    optionValue="flag"
                    optionLabel="descricao"
                    @change="aoTrocarTipoEdicao()"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-6">
                <input-with-label label="Veículo" v-slot="l">
                  <Dropdown
                    :id="l.id"
                    v-model="edicao.entity.flServicoVeiculo"
                    :options="servicosVeiculo"
                    optionValue="flag"
                    optionLabel="descricao"
                    :showClear="true"
                    placeholder="qualquer"
                    :disabled="!tipoPorVeiculo(edicao.entity.flTipoServico)"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-6">
                <input-with-label label="Unidade" v-slot="l" :required="true">
                  <Dropdown
                    :id="l.id"
                    v-model="edicao.entity.flUnidade"
                    :options="unidades"
                    optionValue="flag"
                    optionLabel="descricao"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-6">
                <input-with-label label="Valor" v-slot="l" :required="true">
                  <InputNumber
                    :id="l.id"
                    v-model="edicao.entity.valor"
                    mode="currency"
                    currency="BRL"
                    locale="pt-BR"
                    :min="0"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-6">
                <input-with-label
                  label="Início da vigência"
                  v-slot="l"
                  :required="true"
                >
                  <Calendar
                    :id="l.id"
                    v-model="edicao.entity.dataInicioVigencia"
                    :showIcon="true"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-6">
                <input-with-label label="Fim da vigência" v-slot="l">
                  <Calendar
                    :id="l.id"
                    v-model="edicao.entity.dataFimVigencia"
                    :showIcon="true"
                    :showButtonBar="true"
                  />
                </input-with-label>
              </div>
            </div>
            <small class="rntrc-muted">
              Para mudar um preço a partir de uma data, feche a vigência atual
              (fim) e cadastre uma nova começando no dia seguinte.
            </small>
          </form>
          <template #footer>
            <Button
              type="submit"
              form="rntrc-preco-form"
              label="Salvar"
              class="p-button-rounded"
              :loading="edicao.salvando"
            />
            <Button
              label="Cancelar"
              class="p-button-rounded p-button-text"
              @click="edicao.aberta = false"
            />
          </template>
        </Dialog>
      </template>
    </container>
  </sistema>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  h,
  PropType,
  Ref
} from "vue";
import EntitySelectorCliente from "../../components/cliente/EntitySelectorCliente.vue";
import { ClientePayload } from "../../modules/business/cliente/clienteBusiness";
import { formatDate } from "../../modules/utils";
import { useConfirm, useToast } from "../../modules/message";
import { useRntrcPrecoService } from "../../modules/business/rntrc/rntrcPrecoBusiness";
import {
  RntrcPrecoServicoPayload,
  SERVICOS_VEICULO_RNTRC,
  ServicoVeiculoRntrc,
  TIPOS_SERVICO_RNTRC,
  TipoServicoRntrc,
  UNIDADES_PRECO_RNTRC,
  UnidadePrecoRntrc,
  formatMoeda,
  getServicoVeiculoLabel,
  getTipoServicoLabel,
  getUnidadePrecoLabel
} from "../../modules/business/rntrc/rntrcTypes";

/** Inclusão tem preço por tipo de veículo (trator x carreta). */
const TIPOS_POR_VEICULO: Array<TipoServicoRntrc> = ["IMF", "IAR"];
/** Cobrados por placa; os demais por solicitação (decisão 2.2). */
const TIPOS_POR_PLACA: Array<TipoServicoRntrc> = [
  "IMF",
  "IAR",
  "EXC",
  "TRF",
  "ALT"
];

interface CelulaPreco {
  proprio: RntrcPrecoServicoPayload | null;
  padrao: RntrcPrecoServicoPayload | null;
  herdado: boolean;
}

interface LinhaMatriz {
  flTipoServico: TipoServicoRntrc;
  descricao: string;
  unidade: UnidadePrecoRntrc;
  porVeiculo: boolean;
  principal: CelulaPreco;
  carreta: CelulaPreco;
}

interface RntrcPrecoState {
  stateReady: boolean;
  loadings: Array<Ref<boolean>>;
  cliente: ClientePayload | null;
  precosPadrao: Array<RntrcPrecoServicoPayload>;
  precosCliente: Array<RntrcPrecoServicoPayload>;
  edicao: {
    aberta: boolean;
    salvando: boolean;
    entity: RntrcPrecoServicoPayload;
  };
}

const inicioDoDia = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Célula da matriz: valor próprio ou herdado do padrão. */
const RntrcPrecoCelula = defineComponent({
  props: {
    celula: { type: Object as PropType<CelulaPreco>, required: true }
  },
  emits: ["editar"],
  setup(props, { emit }) {
    return () => {
      const c = props.celula;
      const preco = c.proprio ?? c.padrao;
      const filhos = [];
      if (preco) {
        filhos.push(
          h(
            "span",
            { class: c.herdado ? "rntrc-muted" : "rntrc-valor" },
            (c.herdado ? "padrão " : "") + formatMoeda(preco.valor)
          )
        );
        if (
          preco.dataInicioVigencia &&
          inicioDoDia(preco.dataInicioVigencia).getTime() >
            inicioDoDia(new Date()).getTime()
        ) {
          filhos.push(
            h(
              "small",
              { class: "rntrc-muted p-ml-1" },
              "a partir de " + formatDate(preco.dataInicioVigencia)
            )
          );
        }
      } else {
        filhos.push(h("span", { class: "rntrc-muted" }, "sem preço"));
      }
      filhos.push(
        h(
          "a",
          {
            class: "p-ml-2 rntrc-link",
            href: "javascript:void(0)",
            onClick: () => emit("editar")
          },
          c.proprio ? "editar" : "definir"
        )
      );
      return h("span", {}, filhos);
    };
  }
});

export default defineComponent({
  setup() {
    const hoje = inicioDoDia(new Date());
    const state = reactive<RntrcPrecoState>({
      stateReady: false,
      loadings: [],
      cliente: null,
      precosPadrao: [],
      precosCliente: [],
      edicao: { aberta: false, salvando: false, entity: {} }
    });

    const stateLoading = computed(() => state.loadings.length !== 0);

    const removeLoading = (loading: Ref<boolean>) => {
      const idx = state.loadings.indexOf(loading);
      if (idx >= 0) {
        state.loadings.splice(idx, 1);
      }
    };

    const tipoPorVeiculo = (tipo?: string | null) =>
      TIPOS_POR_VEICULO.indexOf(tipo as TipoServicoRntrc) >= 0;

    const unidadePadrao = (tipo?: string | null): UnidadePrecoRntrc =>
      TIPOS_POR_PLACA.indexOf(tipo as TipoServicoRntrc) >= 0 ? "PLA" : "SOL";

    const isVigente = (p: RntrcPrecoServicoPayload) =>
      !!p.dataInicioVigencia &&
      inicioDoDia(p.dataInicioVigencia).getTime() <= hoje.getTime() &&
      (!p.dataFimVigencia ||
        inicioDoDia(p.dataFimVigencia).getTime() >= hoje.getTime());

    /** Vigente hoje; se não houver, a próxima vigência futura. */
    const escolher = (
      lista: Array<RntrcPrecoServicoPayload>,
      tipo: TipoServicoRntrc,
      servico: ServicoVeiculoRntrc | null
    ): RntrcPrecoServicoPayload | null => {
      const candidatos = lista.filter(
        (p) =>
          p.flTipoServico === tipo && (p.flServicoVeiculo ?? null) === servico
      );
      const vigentes = candidatos
        .filter(isVigente)
        .sort(
          (a, b) =>
            (b.dataInicioVigencia?.getTime() ?? 0) -
            (a.dataInicioVigencia?.getTime() ?? 0)
        );
      if (vigentes.length) {
        return vigentes[0];
      }
      const futuros = candidatos
        .filter(
          (p) =>
            !!p.dataInicioVigencia &&
            inicioDoDia(p.dataInicioVigencia).getTime() > hoje.getTime()
        )
        .sort(
          (a, b) =>
            (a.dataInicioVigencia?.getTime() ?? 0) -
            (b.dataInicioVigencia?.getTime() ?? 0)
        );
      return futuros.length ? futuros[0] : null;
    };

    const celula = (
      tipo: TipoServicoRntrc,
      servico: ServicoVeiculoRntrc | null
    ): CelulaPreco => {
      // resolução igual à do backend: AUT/IMP específico, depois "qualquer"
      const padrao =
        escolher(state.precosPadrao, tipo, servico) ??
        (servico ? escolher(state.precosPadrao, tipo, null) : null);
      if (!state.cliente) {
        return { proprio: padrao, padrao: null, herdado: false };
      }
      const proprio =
        escolher(state.precosCliente, tipo, servico) ??
        (servico ? escolher(state.precosCliente, tipo, null) : null);
      return { proprio, padrao, herdado: !proprio };
    };

    const matriz = computed<Array<LinhaMatriz>>(() =>
      TIPOS_SERVICO_RNTRC.map((t) => {
        const porVeiculo = tipoPorVeiculo(t.flag);
        return {
          flTipoServico: t.flag,
          descricao: t.descricao,
          unidade: unidadePadrao(t.flag),
          porVeiculo,
          principal: celula(t.flag, porVeiculo ? "AUT" : null),
          carreta: celula(t.flag, porVeiculo ? "IMP" : null)
        };
      })
    );

    const linhasEscopo = computed(() =>
      (state.cliente ? state.precosCliente : state.precosPadrao)
        .slice()
        .sort(
          (a, b) =>
            TIPOS_SERVICO_RNTRC.findIndex((t) => t.flag === a.flTipoServico) -
              TIPOS_SERVICO_RNTRC.findIndex(
                (t) => t.flag === b.flTipoServico
              ) ||
            (a.flServicoVeiculo ?? "").localeCompare(
              b.flServicoVeiculo ?? ""
            ) ||
            (b.dataInicioVigencia?.getTime() ?? 0) -
              (a.dataInicioVigencia?.getTime() ?? 0)
        )
    );

    const tituloMatriz = computed(() =>
      state.cliente
        ? "Preço negociado — " + state.cliente.nome
        : "Tabela padrão (global)"
    );

    const carregar = () => {
      const padrao = useRntrcPrecoService();
      state.loadings.push(padrao.loading);
      padrao
        .listarRoot(null)
        .then(() => {
          state.precosPadrao = (Array.isArray(padrao.data.value)
            ? padrao.data.value
            : []
          ).filter((p: RntrcPrecoServicoPayload) => !p.idCliente);
          removeLoading(padrao.loading);
        })
        .catch(() => removeLoading(padrao.loading));

      if (state.cliente?.idCliente) {
        const doCliente = useRntrcPrecoService();
        state.loadings.push(doCliente.loading);
        doCliente
          .listarRoot(state.cliente.idCliente)
          .then(() => {
            state.precosCliente = (Array.isArray(doCliente.data.value)
              ? doCliente.data.value
              : []
            ).filter(
              (p: RntrcPrecoServicoPayload) =>
                p.idCliente === state.cliente?.idCliente
            );
            removeLoading(doCliente.loading);
          })
          .catch(() => removeLoading(doCliente.loading));
      } else {
        state.precosCliente = [];
      }
    };

    const confirmSelectionCliente = (c?: ClientePayload | null) => {
      state.cliente = c && c.idCliente ? c : null;
      carregar();
    };

    const abrirEdicao = (entity: RntrcPrecoServicoPayload) => {
      state.edicao.entity = entity;
      state.edicao.salvando = false;
      state.edicao.aberta = true;
    };

    const novoPreco = (
      tipo?: TipoServicoRntrc | null,
      servico?: ServicoVeiculoRntrc | null
    ) => {
      abrirEdicao({
        idCliente: state.cliente?.idCliente ?? null,
        flTipoServico: tipo ?? null,
        flServicoVeiculo: servico ?? null,
        flUnidade: tipo ? unidadePadrao(tipo) : null,
        valor: null,
        dataInicioVigencia: hoje,
        dataFimVigencia: null
      });
    };

    const editarLinha = (p: RntrcPrecoServicoPayload) => {
      abrirEdicao({ ...p });
    };

    const editarCelula = (
      linha: LinhaMatriz,
      servico: ServicoVeiculoRntrc | null
    ) => {
      const c = servico === "IMP" ? linha.carreta : linha.principal;
      if (c.proprio && c.proprio.idRntrcPrecoServico) {
        editarLinha(c.proprio);
      } else {
        novoPreco(linha.flTipoServico, servico);
        if (c.padrao) {
          state.edicao.entity.valor = c.padrao.valor ?? null;
          state.edicao.entity.flUnidade =
            c.padrao.flUnidade ?? state.edicao.entity.flUnidade;
        }
      }
    };

    const aoTrocarTipoEdicao = () => {
      const tipo = state.edicao.entity.flTipoServico;
      if (!tipoPorVeiculo(tipo)) {
        state.edicao.entity.flServicoVeiculo = null;
      }
      state.edicao.entity.flUnidade = unidadePadrao(tipo);
    };

    const salvar = () => {
      const e = state.edicao.entity;
      if (!e.flTipoServico || !e.flUnidade) {
        useToast().warning("Informe o serviço e a unidade.");
        return;
      }
      if (e.valor === null || typeof e.valor === "undefined" || e.valor < 0) {
        useToast().warning("Informe o valor (zero para bonificado).");
        return;
      }
      if (!e.dataInicioVigencia) {
        useToast().warning("Informe o início da vigência.");
        return;
      }
      if (
        e.dataFimVigencia &&
        e.dataFimVigencia.getTime() < e.dataInicioVigencia.getTime()
      ) {
        useToast().warning("O fim da vigência é anterior ao início.");
        return;
      }
      const payload: RntrcPrecoServicoPayload = {
        idRntrcPrecoServico: e.idRntrcPrecoServico ?? null,
        idCliente: state.cliente?.idCliente ?? null,
        flTipoServico: e.flTipoServico,
        flServicoVeiculo: tipoPorVeiculo(e.flTipoServico)
          ? e.flServicoVeiculo ?? null
          : null,
        flUnidade: e.flUnidade,
        valor: e.valor,
        dataInicioVigencia: e.dataInicioVigencia,
        dataFimVigencia: e.dataFimVigencia ?? null
      };
      const service = useRntrcPrecoService();
      state.edicao.salvando = true;
      (payload.idRntrcPrecoServico
        ? service.atualizar(payload.idRntrcPrecoServico, payload)
        : service.criar(payload)
      )
        .then(() => {
          state.edicao.aberta = false;
          useToast().success("Preço salvo.");
          carregar();
        })
        .finally(() => {
          state.edicao.salvando = false;
        });
    };

    const removerLinha = (p: RntrcPrecoServicoPayload) => {
      const id = p.idRntrcPrecoServico;
      if (!id) {
        return;
      }
      useConfirm().require({
        message:
          "Excluir o preço de " +
          getTipoServicoLabel(p.flTipoServico) +
          (p.flServicoVeiculo
            ? " (" + getServicoVeiculoLabel(p.flServicoVeiculo) + ")"
            : "") +
          " de " +
          formatMoeda(p.valor) +
          "? Solicitações já criadas mantêm o valor gravado.",
        header: "Excluir preço",
        icon: "pi pi-exclamation-triangle",
        acceptClass: "p-button-danger",
        accept: () => {
          const { loading, remover } = useRntrcPrecoService();
          state.loadings.push(loading);
          remover(id)
            .then(() => {
              removeLoading(loading);
              useToast().success("Preço excluído.");
              carregar();
            })
            .catch(() => removeLoading(loading));
        }
      });
    };

    onMounted(() => {
      state.stateReady = true;
      carregar();
    });

    return {
      hoje,
      stateLoading,
      matriz,
      linhasEscopo,
      tituloMatriz,
      tiposDisponiveis: TIPOS_SERVICO_RNTRC,
      servicosVeiculo: SERVICOS_VEICULO_RNTRC,
      unidades: UNIDADES_PRECO_RNTRC,
      tipoPorVeiculo,
      isVigente,
      confirmSelectionCliente,
      novoPreco,
      editarLinha,
      editarCelula,
      aoTrocarTipoEdicao,
      salvar,
      removerLinha,
      formatDate,
      formatMoeda,
      getServicoVeiculoLabel,
      getTipoServicoLabel,
      getUnidadePrecoLabel,
      ...toRefs(state)
    };
  },
  components: {
    EntitySelectorCliente,
    RntrcPrecoCelula
  }
});
</script>

<style scoped lang="scss">
.rntrc-ajuda {
  font-size: $font-size-sm;
  color: $text-secondary;
}

:deep(.rntrc-muted),
.rntrc-muted {
  color: $text-muted;
}

:deep(.rntrc-valor) {
  font-weight: $font-weight-semibold;
}

:deep(.rntrc-link) {
  font-size: $font-size-xs;
  cursor: pointer;
}
</style>
